import { mapValues, values } from 'lodash';

import { tabs } from '../../components/RateCorrectionsEditor/RateCorrectionsEditor';
import { RATE_CORRECTION_DEFAULT_VALUE } from '../../constants';
import { getRandomId } from '../../service/IdHelpers/IdHelpers';
import {
    cloudinessTypesByValue,
    cloudinessValuesByType,
    getRateValueFromRateControlValue,
    rainfallTypesByValue,
    rainfallValuesByType,
    trafficValuesByType
} from '../../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import { EditCampaignData } from '../../typings/campaignsEdit';
import { CampaignType, GroupType } from '../../typings/domain';
import { ExperimentsValue } from '../../typings/experimentsEditor';
import {
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers,
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments,
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierDemographicsAdjustment,
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierExpressAdjustment,
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierMobileAdjustment,
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierRetargetingAdjustment,
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierWeatherAdjustment,
    adGroupsRowFragmentForEdit_GdCpmOutdoorAdGroup_bidModifiers_adjustments_GdBidModifierAdTypeAdjustment,
    adGroupsRowFragmentForEdit_GdCpmOutdoorAdGroup_bidModifiers_adjustments_GdBidModifierInventoryAdjustment,
    BidModifierExpressionOperatorInput,
    BidModifierExpressionParameterInput,
    CampaignsEditDataQuery_client_campaigns_rowset_GdContentPromotionCampaign_bidModifiers_adjustments_GdBidModifierABSegmentAdjustment,
    CampaignsEditDataQuery_client_clientConstants_allowedBidModifierTypesByCampaignTypes,
    GdAdGroupType,
    GdAdjustmentAdType,
    GdAdjustmentAdTypeInput,
    GdAgeType,
    GdAgeTypeInput,
    GdBidModifierType,
    GdBidModifierTypeInput,
    GdGenderType,
    GdGenderTypeInput,
    GdInventoryType,
    GdInventoryTypeInput,
    GdOperationTypeInput,
    GdOsType,
    GdOsTypeInput,
    GdUpdateBidModifierABSegmentInput,
    GdUpdateBidModifiersInput,
    GdUpdateBidModifierWeatherAdjustmentItemInput,
    GdUpdateBidModifierWeatherExpressionInput,
    GdWeatherType,
    GdWeatherTypeInput,
    GroupsQueryForEdit_client_clientConstants_allowedBidModifiersValues,
    GroupsQueryForEdit_client_clientConstants_allowedBidModifierTypesByAdGroupTypes
} from '../../typings/graphql-grid';
import { ConditionsCorrectionsKeys, ProductsRestrictionNestedConditions } from '../../typings/groupsEditState';
import {
    ABSegmentsRateCorrection,
    BaseRatesCorrectionsConstants,
    CloudinessTypes,
    CommonRateCorrectionCondition,
    DemographyRateCorrectionCondition,
    DeviceRateCorrectionCondition,
    DeviceRateCorrectionType,
    EditorRateCorrection,
    EditorWeatherRateCorrectionCondition,
    ExtraRateCorrectionType,
    INVENTORY_AD_TYPE_CORRECTIONS,
    INVENTORY_VIDEO_CORRECTIONS,
    InventoryRateCorrectionCondition,
    InventoryRateCorrectionType,
    RainfallTypes,
    RateControlMode,
    RateControlValue,
    RateCorrection,
    RateCorrectionAge,
    RateCorrectionCondition,
    RateCorrectionGender,
    RateCorrectionMode,
    RateCorrectionType,
    RatesCorrectionsConstantsData,
    RatesCorrectionsInventoryConstants,
    RatesCorrectionsMobileConstants,
    RatesCorrectionsValue,
    RetargetingRateCorrectionCondition,
    TrafficJamRateCorrectionCondition,
    TrafficJamRateCorrectionsType,
    WeatherRateCorrection,
    WeatherRateCorrectionCondition,
    WeatherRateCorrectionConditionCloudiness,
    WeatherRateCorrectionConditionRainfall,
    WeatherRateCorrectionConditionTemperature,
    WeatherRateCorrectionsType
} from '../../typings/ratesCorrections';
import { mapCampaignRowTypeToCampaignType, mapGroupRowTypeToGroupType } from '../common/common';

export function mapRateCorrectionsData(
    rateCorrectionsFromApi: Array<adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers> | null,
    availableBidModifiersTypesArr: Array<GdBidModifierType>
): RatesCorrectionsValue {
    const availableBidModifiersTypes = new Set(availableBidModifiersTypesArr);
    return Object.keys(RateCorrectionType).reduce(
        (res: RatesCorrectionsValue, type: RateCorrectionType): RatesCorrectionsValue => {
            const rateCorrectionData = mapRateCorrectionDataFromApiByType(
                type,
                rateCorrectionsFromApi,
                availableBidModifiersTypes
            );

            if (rateCorrectionData) {
                res[type] = rateCorrectionData;
            }

            return res;
        },
        <RatesCorrectionsValue>{}
    );
}

export function mapRateCorrectionsCpmBannerData(
    rateCorrectionsFromApi: Array<adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers> | null,
    rates?: ProductsRestrictionNestedConditions[]
): RatesCorrectionsValue {
    const availibleBitModifitFull = new Set([
        GdBidModifierType.WEATHER_MULTIPLIER,
        GdBidModifierType.SMART_TGO_MULTIPLIER,
        GdBidModifierType.DESKTOP_MULTIPLIER,
        GdBidModifierType.MOBILE_MULTIPLIER,
        GdBidModifierType.VIDEO_MULTIPLIER,
        GdBidModifierType.RETARGETING_MULTIPLIER,
        GdBidModifierType.DEMOGRAPHY_MULTIPLIER,
        GdBidModifierType.EXPRESS_TRAFFIC_MULTIPLIER
    ]);
    const ratesForMap = rates ? rates : [];

    return ratesForMap
        .sort((a, b) => tabs.indexOf(makeRateCorrectionTypeDataBy(a)) - tabs.indexOf(makeRateCorrectionTypeDataBy(b)))
        .reduce(
            (res: RatesCorrectionsValue, type: ConditionsCorrectionsKeys): RatesCorrectionsValue => {
                const rateType = makeRateCorrectionTypeDataBy(type);
                const rateCorrectionData = mapRateCorrectionDataFromApiByType(
                    rateType,
                    rateCorrectionsFromApi,
                    availibleBitModifitFull
                );

                if (rateType && rateCorrectionData) {
                    res[rateType] = rateCorrectionData;
                }

                return res;
            },
            <RatesCorrectionsValue>{}
        );
}

function makeRateCorrectionTypeDataBy(rate: ProductsRestrictionNestedConditions): RateCorrectionType {
    switch (rate) {
        case ConditionsCorrectionsKeys.device:
            return RateCorrectionType.device;
        case ConditionsCorrectionsKeys.socdem:
            return RateCorrectionType.demography;
        case ConditionsCorrectionsKeys.target_audience:
            return RateCorrectionType.retargeting;
        case ConditionsCorrectionsKeys.weather:
            return RateCorrectionType.weather;
        case ConditionsCorrectionsKeys.traffic:
            return RateCorrectionType.trafficJam;
        default:
            return RateCorrectionType.device;
    }
}

export function getRateControlValueFromRateValue(rateValue?: number): RateControlValue {
    if (rateValue === undefined) {
        return {
            mode: RateControlMode.up,
            value: undefined
        };
    }

    if (rateValue < 0) {
        return {
            mode: RateControlMode.down,
            value: Math.abs(rateValue)
        };
    }

    return {
        mode: RateControlMode.up,
        value: rateValue
    };
}

export function getRateCorrectionValueFromMultiplier(multiplier: number): number {
    return multiplier - RATE_CORRECTION_DEFAULT_VALUE;
}

export function getMultiplierFromRateCorrectionValue(value: number): number {
    return value + RATE_CORRECTION_DEFAULT_VALUE;
}

export function mapEditorRateCorrectionsToRateCorrections(
    editorRateCorrections: Record<RateCorrectionType, EditorRateCorrection>
): Record<RateCorrectionType, RateCorrection> {
    return mapValues(
        editorRateCorrections,
        (editorTabValue: EditorRateCorrection): RateCorrection => {
            const { singleMassRateControlValue } = editorTabValue;

            const baseParams = {
                singleMassRateControlValue:
                    singleMassRateControlValue !== undefined && singleMassRateControlValue.value !== undefined
                        ? getRateValueFromRateControlValue(singleMassRateControlValue)
                        : undefined
            };
            switch (editorTabValue.type) {
                case RateCorrectionType.retargeting:
                    return {
                        ...baseParams,
                        type: RateCorrectionType.retargeting,
                        isSwitchedOn: editorTabValue.isSwitchedOn,
                        items: editorTabValue.items.reduce(
                            (
                                res: Array<RetargetingRateCorrectionCondition>,
                                { id, serverId, value, retargetingConditionId }
                            ) => {
                                if (value.value !== undefined) {
                                    res.push({
                                        id,
                                        serverId,
                                        retargetingConditionId,
                                        value: getRateValueFromRateControlValue(value)
                                    });
                                }

                                return res;
                            },
                            []
                        )
                    };

                case RateCorrectionType.device:
                    return {
                        ...baseParams,
                        type: RateCorrectionType.device,
                        isSwitchedOn: editorTabValue.isSwitchedOn,
                        items: editorTabValue.items.reduce(
                            (res: Array<DeviceRateCorrectionCondition>, { id, serverId, value, deviceType }) => {
                                if (value.value !== undefined) {
                                    res.push({
                                        id,
                                        deviceType,
                                        serverId,
                                        value: getRateValueFromRateControlValue(value)
                                    });
                                }

                                return res;
                            },
                            []
                        )
                    };

                case RateCorrectionType.inventory:
                    return {
                        ...baseParams,
                        type: RateCorrectionType.inventory,
                        isSwitchedOn: editorTabValue.isSwitchedOn,
                        items: editorTabValue.items.reduce(
                            (res: Array<InventoryRateCorrectionCondition>, { id, serverId, value, inventoryType }) => {
                                if (value.value !== undefined) {
                                    res.push({
                                        id,
                                        inventoryType,
                                        serverId,
                                        value: getRateValueFromRateControlValue(value)
                                    });
                                }

                                return res;
                            },
                            []
                        )
                    };

                case RateCorrectionType.demography:
                    return {
                        ...baseParams,
                        type: RateCorrectionType.demography,
                        isSwitchedOn: editorTabValue.isSwitchedOn,
                        items: editorTabValue.items.reduce(
                            (res: Array<DemographyRateCorrectionCondition>, { id, serverId, age, gender, value }) => {
                                if (age && gender && value.value !== undefined) {
                                    res.push({
                                        id,
                                        serverId,
                                        age,
                                        gender,
                                        value: getRateValueFromRateControlValue(value)
                                    });
                                }

                                return res;
                            },
                            []
                        )
                    };

                case RateCorrectionType.videoAddition:
                    return {
                        ...baseParams,
                        type: RateCorrectionType.videoAddition,
                        items: editorTabValue.items.reduce(
                            (res: Array<CommonRateCorrectionCondition>, { id, serverId, value }) => {
                                if (value.value !== undefined) {
                                    res.push({
                                        id,
                                        serverId,
                                        value: getRateValueFromRateControlValue(value)
                                    });
                                }

                                return res;
                            },
                            []
                        )
                    };

                case RateCorrectionType.weather:
                    return {
                        ...baseParams,
                        type: RateCorrectionType.weather,
                        isSwitchedOn: editorTabValue.isSwitchedOn,
                        mode: editorTabValue.mode,
                        items: editorTabValue.items.reduce(
                            (
                                res: Array<WeatherRateCorrectionCondition>,
                                item: EditorWeatherRateCorrectionCondition
                            ) => {
                                const baseWeatherDataItem = {
                                    id: item.id,
                                    serverId: item.serverId,
                                    value:
                                        editorTabValue.mode === RateCorrectionMode.oneDone &&
                                        item.value.value !== undefined
                                            ? getRateValueFromRateControlValue(item.value)
                                            : undefined
                                };

                                switch (item.weatherType) {
                                    case WeatherRateCorrectionsType.temperature:
                                        res.push({
                                            ...baseWeatherDataItem,
                                            weatherType: WeatherRateCorrectionsType.temperature,
                                            minTemperature: item.minTemperature,
                                            maxTemperature: item.maxTemperature
                                        });

                                        break;
                                    case WeatherRateCorrectionsType.cloudiness:
                                        res.push({
                                            ...baseWeatherDataItem,
                                            weatherType: WeatherRateCorrectionsType.cloudiness,
                                            cloudinessIds: item.cloudinessIds
                                        });

                                        break;
                                    case WeatherRateCorrectionsType.rainfall:
                                        res.push({
                                            ...baseWeatherDataItem,
                                            weatherType: WeatherRateCorrectionsType.rainfall,
                                            rainfallIds: item.rainfallIds
                                        });

                                        break;
                                    default:
                                        throw new Error('Неизвестный тип погоды');
                                }

                                return res;
                            },
                            []
                        )
                    };

                case RateCorrectionType.smartBanners:
                    return {
                        ...baseParams,
                        type: RateCorrectionType.smartBanners,
                        items: editorTabValue.items.reduce(
                            (res: Array<CommonRateCorrectionCondition>, { id, serverId, value }) => {
                                if (value.value !== undefined) {
                                    res.push({
                                        id,
                                        serverId,
                                        value: getRateValueFromRateControlValue(value)
                                    });
                                }

                                return res;
                            },
                            []
                        )
                    };

                case RateCorrectionType.trafficJam:
                    return {
                        ...baseParams,
                        type: RateCorrectionType.trafficJam,
                        isSwitchedOn: editorTabValue.isSwitchedOn,
                        items: editorTabValue.items.reduce(
                            (
                                res: Array<TrafficJamRateCorrectionCondition>,
                                { id, serverId, value, trafficJamType }
                            ) => {
                                if (value.value !== undefined) {
                                    res.push({
                                        id,
                                        trafficJamType,
                                        serverId,
                                        value: getRateValueFromRateControlValue(value)
                                    });
                                }

                                return res;
                            },
                            []
                        )
                    };

                default:
                    throw Error('Неподдерживаемый тип корректировки ставки');
            }
        }
    );
}

export function mapRateCorrectionsToEditorRateCorrections(
    groupRateCorrectionsValue: RatesCorrectionsValue
): Record<RateCorrectionType, EditorRateCorrection> {
    return mapValues(
        groupRateCorrectionsValue,
        (rateCorrection: RateCorrection): EditorRateCorrection => {
            const items: (RateCorrectionCondition)[] = rateCorrection.items;

            return {
                ...rateCorrection,
                hasChanges: false,
                errors: [],
                items: items.map(item => {
                    return {
                        ...item,
                        value: getRateControlValueFromRateValue(item.value),
                        errors: {}
                    };
                }),
                singleMassRateControlValue: getRateControlValueFromRateValue(rateCorrection.singleMassRateControlValue)
            } as EditorRateCorrection;
        }
    );
}

export function mapRateCorrectionAgeFromApiFormat(age: GdAgeType | null): RateCorrectionAge {
    switch (age) {
        case GdAgeType._0_17:
            return RateCorrectionAge.age0_17;

        case GdAgeType._18_24:
            return RateCorrectionAge.age18_24;

        case GdAgeType._25_34:
            return RateCorrectionAge.age25_34;

        case GdAgeType._35_44:
            return RateCorrectionAge.age35_44;

        case GdAgeType._45_54:
            return RateCorrectionAge.age45_54;

        case GdAgeType._55_:
            return RateCorrectionAge.age55_;

        default:
            return RateCorrectionAge.all;
    }
}

export function mapRateCorrectionAgeToApiFormat(age: RateCorrectionAge): GdAgeTypeInput | null {
    switch (age) {
        case RateCorrectionAge.age0_17:
            return GdAgeTypeInput._0_17;

        case RateCorrectionAge.age18_24:
            return GdAgeTypeInput._18_24;

        case RateCorrectionAge.age25_34:
            return GdAgeTypeInput._25_34;

        case RateCorrectionAge.age35_44:
            return GdAgeTypeInput._35_44;

        case RateCorrectionAge.age45_54:
            return GdAgeTypeInput._45_54;

        case RateCorrectionAge.age55_:
            return GdAgeTypeInput._55_;

        case RateCorrectionAge.all:
            return null;

        default:
            return null;
    }
}

export function mapRateCorrectionGenderFromApiFormat(gender: GdGenderType | null): RateCorrectionGender {
    switch (gender) {
        case GdGenderType.FEMALE:
            return RateCorrectionGender.female;

        case GdGenderType.MALE:
            return RateCorrectionGender.male;

        default:
            return RateCorrectionGender.all;
    }
}

export function mapRateCorrectionGenderToApiFormat(gender: RateCorrectionGender): GdGenderTypeInput | null {
    switch (gender) {
        case RateCorrectionGender.female:
            return GdGenderTypeInput.FEMALE;

        case RateCorrectionGender.male:
            return GdGenderTypeInput.MALE;

        case RateCorrectionGender.all:
            return null;

        default:
            return null;
    }
}

export function mapRateCorrectionOsTypeFromApiFormat(osType: GdOsType | null): DeviceRateCorrectionType {
    switch (osType) {
        case GdOsType.ANDROID:
            return DeviceRateCorrectionType.android;

        case GdOsType.IOS:
            return DeviceRateCorrectionType.ios;

        default:
            return DeviceRateCorrectionType.allMobile;
    }
}

export function mapRateCorrectionInventoryTypeFromApiFormat(type: GdInventoryType | null): InventoryRateCorrectionType {
    switch (type) {
        case GdInventoryType.INBANNER:
            return InventoryRateCorrectionType.inbanner;

        case GdInventoryType.INPAGE:
            return InventoryRateCorrectionType.inpage;

        case GdInventoryType.INSTREAM_WEB:
            return InventoryRateCorrectionType.instreamWeb;

        case GdInventoryType.INTERSTITIAL:
            return InventoryRateCorrectionType.interstitial;

        case GdInventoryType.REWARDED:
            return InventoryRateCorrectionType.rewarded;

        default:
            throw 'Неподдерживаемый тип корректировки инвентаря видео!';
    }
}

export function mapRateCorrectionInventoryTypeToApiFormat(
    type: InventoryRateCorrectionType
): GdInventoryTypeInput | null {
    switch (type) {
        case InventoryRateCorrectionType.inbanner:
            return GdInventoryTypeInput.INBANNER;

        case InventoryRateCorrectionType.inpage:
            return GdInventoryTypeInput.INPAGE;

        case InventoryRateCorrectionType.instreamWeb:
            return GdInventoryTypeInput.INSTREAM_WEB;

        case InventoryRateCorrectionType.interstitial:
            return GdInventoryTypeInput.INTERSTITIAL;

        case InventoryRateCorrectionType.rewarded:
            return GdInventoryTypeInput.REWARDED;

        default:
            return null;
    }
}

export function mapRateCorrectionInventoryTypeFromApiAdTypeFormat(
    type: GdAdjustmentAdType | null
): InventoryRateCorrectionType {
    switch (type) {
        case GdAdjustmentAdType.CPM_BANNER:
            return InventoryRateCorrectionType.cpmBanner;

        default:
            throw 'Неподдерживаемый тип корректировок на инвентарь!';
    }
}

export function mapRateCorrectionInventoryTypeToApiAdTypeFormat(
    type: InventoryRateCorrectionType
): GdAdjustmentAdTypeInput | null {
    switch (type) {
        case InventoryRateCorrectionType.cpmBanner:
            return GdAdjustmentAdTypeInput.CPM_BANNER;

        default:
            return null;
    }
}

export function mapABSegmentsRateCorrectionsData(
    rateCorrectionsFromApi: adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers[] | null
): ABSegmentsRateCorrection {
    const emptyRateCorrectionsData = {
        isSwitchedOn: false,
        items: [],
        type: ExtraRateCorrectionType.abSegments
    };

    if (rateCorrectionsFromApi === null) {
        return emptyRateCorrectionsData;
    }

    const abSegmentsRateCorrectionFromApi = rateCorrectionsFromApi.find(rateCorrection => {
        return rateCorrection.type === GdBidModifierType.AB_SEGMENT_MULTIPLIER;
    });

    if (!abSegmentsRateCorrectionFromApi) {
        return emptyRateCorrectionsData;
    } else {
        return {
            type: ExtraRateCorrectionType.abSegments,
            isSwitchedOn: abSegmentsRateCorrectionFromApi.enabled,
            items: abSegmentsRateCorrectionFromApi.adjustments.map(
                (
                    adjustment: CampaignsEditDataQuery_client_campaigns_rowset_GdContentPromotionCampaign_bidModifiers_adjustments_GdBidModifierABSegmentAdjustment
                ) => {
                    return {
                        id: adjustment.id,
                        serverId: adjustment.id,
                        segmentId: adjustment.segmentId,
                        sectionId: adjustment.sectionId,
                        value: adjustment.percent
                    };
                }
            )
        };
    }
}

export function mapABSegmentsRateCorrectionsToApi(
    campaign: EditCampaignData,
    experimentsValue: ExperimentsValue,
    isCreateCampaignMapping?: boolean
): GdUpdateBidModifierABSegmentInput | undefined {
    if (!campaign.enabledFields.experiments) {
        return;
    }

    const adjustments = values(experimentsValue.correctionsMap).reduce((acc, rateCorrection) => {
        return rateCorrection.value !== RATE_CORRECTION_DEFAULT_VALUE
            ? [
                  ...acc,
                  {
                      id: rateCorrection.serverId,
                      sectionId: rateCorrection.sectionId,
                      segmentId: rateCorrection.segmentId,
                      percent: rateCorrection.value
                  }
              ]
            : acc;
    }, []);

    if (adjustments.length === 0) {
        return;
    }

    return {
        type: GdBidModifierTypeInput.AB_SEGMENT_MULTIPLIER,
        campaignId: isCreateCampaignMapping ? '0' : campaign.id,
        enabled: experimentsValue.areCorrectionsEnabled,
        adjustments
    };
}

export function mapRateCorrectionDataFromApiByType(
    type: RateCorrectionType,
    rateCorrectionsFromApi: Array<adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers> | null,
    availableBidModifiersTypes: Set<GdBidModifierType>
): RateCorrection | undefined {
    const emptyRateCorrectionsData = {
        isSwitchedOn: true,
        items: []
    };

    if (rateCorrectionsFromApi === null) {
        rateCorrectionsFromApi = [];
    }

    switch (type) {
        case RateCorrectionType.demography: {
            const apiType = GdBidModifierType.DEMOGRAPHY_MULTIPLIER;

            if (!availableBidModifiersTypes.has(apiType)) {
                return undefined;
            }

            const data = rateCorrectionsFromApi.find(
                rateCorrectionFromApi => rateCorrectionFromApi !== null && rateCorrectionFromApi.type === apiType
            );

            if (!data) {
                return {
                    type: type,
                    ...emptyRateCorrectionsData
                };
            } else {
                return {
                    isSwitchedOn: data.enabled,
                    type: type,
                    items: data.adjustments.reduce(
                        (
                            res: Array<DemographyRateCorrectionCondition>,
                            adjustment: adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierDemographicsAdjustment
                        ) => {
                            // раньше было значение 45+,
                            // в задаче https://st.yandex-team.ru/DIRECT-66902 вместо 45+ появились 2 новых значения: 45-54 и 55+
                            // данное условие обеспечивает обратную совместимость и преобразует корректировку со значением 45+ в две
                            if (adjustment.age === GdAgeType._45_) {
                                const baseRateParams = {
                                    id: adjustment.id,
                                    serverId: adjustment.id,
                                    value: getRateCorrectionValueFromMultiplier(adjustment.percent),
                                    gender: mapRateCorrectionGenderFromApiFormat(adjustment.gender)
                                };

                                res = res.concat([
                                    {
                                        ...baseRateParams,
                                        age: mapRateCorrectionAgeFromApiFormat(GdAgeType._45_54)
                                    },
                                    {
                                        ...baseRateParams,
                                        age: mapRateCorrectionAgeFromApiFormat(GdAgeType._55_)
                                    }
                                ]);
                            } else {
                                res.push({
                                    id: adjustment.id,
                                    serverId: adjustment.id,
                                    value: getRateCorrectionValueFromMultiplier(adjustment.percent),
                                    age: mapRateCorrectionAgeFromApiFormat(adjustment.age),
                                    gender: mapRateCorrectionGenderFromApiFormat(adjustment.gender)
                                });
                            }

                            return res;
                        },
                        []
                    )
                };
            }
        }

        case RateCorrectionType.retargeting: {
            const apiType = GdBidModifierType.RETARGETING_MULTIPLIER;

            if (!availableBidModifiersTypes.has(apiType)) {
                return undefined;
            }

            const data = rateCorrectionsFromApi.find(
                rateCorrectionFromApi => rateCorrectionFromApi !== null && rateCorrectionFromApi.type === apiType
            );

            if (!data) {
                return {
                    type: type,
                    ...emptyRateCorrectionsData
                };
            } else {
                return {
                    isSwitchedOn: data.enabled,
                    type: type,
                    items: data.adjustments.map(
                        (
                            adjustment: adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierRetargetingAdjustment
                        ) => {
                            return {
                                id: adjustment.id,
                                serverId: adjustment.id,
                                retargetingConditionId: adjustment.retargetingConditionId,
                                value: getRateCorrectionValueFromMultiplier(adjustment.percent)
                            };
                        }
                    )
                };
            }
        }

        case RateCorrectionType.videoAddition: {
            const apiType = GdBidModifierType.VIDEO_MULTIPLIER;

            if (!availableBidModifiersTypes.has(apiType)) {
                return undefined;
            }

            const data = rateCorrectionsFromApi.find(
                rateCorrectionFromApi => rateCorrectionFromApi !== null && rateCorrectionFromApi.type === apiType
            );

            if (!data) {
                return {
                    type: RateCorrectionType.videoAddition,
                    ...emptyRateCorrectionsData
                };
            } else {
                return {
                    isSwitchedOn: data.enabled,
                    type: RateCorrectionType.videoAddition,
                    items: data.adjustments.map(adjustment => {
                        return {
                            id: adjustment.id,
                            serverId: adjustment.id,
                            value: getRateCorrectionValueFromMultiplier(adjustment.percent)
                        };
                    })
                };
            }
        }

        case RateCorrectionType.device: {
            const desktopsApiType = GdBidModifierType.DESKTOP_MULTIPLIER;
            const mobileApiType = GdBidModifierType.MOBILE_MULTIPLIER;

            const isMobileAvailable = availableBidModifiersTypes.has(mobileApiType);
            const isDesktopsAvailable = availableBidModifiersTypes.has(desktopsApiType);

            if (!isMobileAvailable && !isDesktopsAvailable) {
                return undefined;
            }

            const mobileDeviceData = rateCorrectionsFromApi.find(
                rateCorrectionFromApi => rateCorrectionFromApi !== null && rateCorrectionFromApi.type === mobileApiType
            );

            const desktopsDeviceData = rateCorrectionsFromApi.find(
                rateCorrectionFromApi =>
                    rateCorrectionFromApi !== null && rateCorrectionFromApi.type === desktopsApiType
            );

            if (!mobileDeviceData && !desktopsDeviceData) {
                return {
                    type: RateCorrectionType.device,
                    ...emptyRateCorrectionsData
                };
            } else {
                let mobileAdjustments: Array<DeviceRateCorrectionCondition> = [],
                    deviceAdjustments: Array<DeviceRateCorrectionCondition> = [];

                if (mobileDeviceData) {
                    mobileAdjustments = mobileDeviceData.adjustments.map(
                        (
                            adjustment: adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierMobileAdjustment
                        ) => {
                            return {
                                id: adjustment.id,
                                serverId: adjustment.id,
                                value: getRateCorrectionValueFromMultiplier(adjustment.percent),
                                deviceType: mapRateCorrectionOsTypeFromApiFormat(adjustment.osType)
                            };
                        }
                    );
                }

                if (desktopsDeviceData) {
                    deviceAdjustments = desktopsDeviceData.adjustments.map(
                        (adjustment: adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments) => {
                            return {
                                id: adjustment.id,
                                serverId: adjustment.id,
                                value: getRateCorrectionValueFromMultiplier(adjustment.percent),
                                deviceType: DeviceRateCorrectionType.desktops
                            };
                        }
                    );
                }

                return {
                    type: RateCorrectionType.device,
                    items: [...mobileAdjustments, ...deviceAdjustments]
                };
            }
        }

        case RateCorrectionType.inventory: {
            const videoApiType = GdBidModifierType.INVENTORY_MULTIPLIER;
            const bannersApiType = GdBidModifierType.AD_TYPE_MULTIPLIER;

            const isVideoAvailable = availableBidModifiersTypes.has(videoApiType);
            const isBannersAvailable = availableBidModifiersTypes.has(bannersApiType);

            if (!isVideoAvailable && !isBannersAvailable) {
                return undefined;
            }

            const videoData = rateCorrectionsFromApi.find(
                rateCorrectionFromApi => rateCorrectionFromApi !== null && rateCorrectionFromApi.type === videoApiType
            );

            const bannersData = rateCorrectionsFromApi.find(
                rateCorrectionFromApi => rateCorrectionFromApi !== null && rateCorrectionFromApi.type === bannersApiType
            );

            if (!videoData && !bannersData) {
                return {
                    type: RateCorrectionType.inventory,
                    ...emptyRateCorrectionsData
                };
            } else {
                let videoAdjustments: Array<InventoryRateCorrectionCondition> = [],
                    bannersAdjustments: Array<InventoryRateCorrectionCondition> = [];

                if (videoData) {
                    videoAdjustments = videoData.adjustments.map(
                        (
                            adjustment: adGroupsRowFragmentForEdit_GdCpmOutdoorAdGroup_bidModifiers_adjustments_GdBidModifierInventoryAdjustment
                        ) => {
                            return {
                                id: adjustment.id,
                                serverId: adjustment.id,
                                value: getRateCorrectionValueFromMultiplier(adjustment.percent),
                                inventoryType: mapRateCorrectionInventoryTypeFromApiFormat(adjustment.inventoryType)
                            };
                        }
                    );
                }

                if (bannersData) {
                    bannersAdjustments = bannersData.adjustments.map(
                        (
                            adjustment: adGroupsRowFragmentForEdit_GdCpmOutdoorAdGroup_bidModifiers_adjustments_GdBidModifierAdTypeAdjustment
                        ) => {
                            return {
                                id: adjustment.id,
                                serverId: adjustment.id,
                                value: getRateCorrectionValueFromMultiplier(adjustment.percent),
                                inventoryType: mapRateCorrectionInventoryTypeFromApiAdTypeFormat(adjustment.adType)
                            };
                        }
                    );
                }

                return {
                    type: RateCorrectionType.inventory,
                    items: [...bannersAdjustments, ...videoAdjustments]
                };
            }
        }

        case RateCorrectionType.smartBanners: {
            const apiType = GdBidModifierType.SMART_TGO_MULTIPLIER;

            if (!availableBidModifiersTypes.has(apiType)) {
                return undefined;
            }

            const data = rateCorrectionsFromApi.find(
                rateCorrectionFromApi => rateCorrectionFromApi !== null && rateCorrectionFromApi.type === apiType
            );

            if (!data) {
                return {
                    type: RateCorrectionType.smartBanners,
                    ...emptyRateCorrectionsData
                };
            } else {
                return {
                    isSwitchedOn: data.enabled,
                    type: RateCorrectionType.smartBanners,
                    items: data.adjustments.map(adjustment => {
                        return {
                            id: adjustment.id,
                            serverId: adjustment.id,
                            value: getRateCorrectionValueFromMultiplier(adjustment.percent)
                        };
                    })
                };
            }
        }

        case RateCorrectionType.weather:
            const weatherApiType = GdBidModifierType.WEATHER_MULTIPLIER;

            if (!availableBidModifiersTypes.has(weatherApiType)) {
                return undefined;
            }

            const weatherData = rateCorrectionsFromApi.find(
                rateCorrectionFromApi => rateCorrectionFromApi !== null && rateCorrectionFromApi.type === weatherApiType
            );

            if (weatherData === undefined || weatherData.type !== GdBidModifierType.WEATHER_MULTIPLIER) {
                return {
                    type: RateCorrectionType.weather,
                    items: [],
                    mode: RateCorrectionMode.oneDone
                };
            }

            const weatherAdjustments = weatherData.adjustments as adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierWeatherAdjustment[];

            if (
                weatherAdjustments.length === 1 &&
                weatherAdjustments[0].expression !== null &&
                weatherAdjustments[0].expression.length > 0
            ) {
                // условие означает что структура корректировок соотвествует единой ставке для корректировок,
                // для всех корректировок действует оператор AND

                return mapWeatherConditionsDataFromApiForAllDone(weatherAdjustments);
            } else {
                // условие означает что структура корректировок соотвествует индивидуальной ставке для каждой корректировки,
                // для всех корректировок действует оператор OR

                return mapWeatherConditionsDataFromApiForOneDone(weatherAdjustments);
            }

        case RateCorrectionType.trafficJam: {
            const trafficApiType = GdBidModifierType.EXPRESS_TRAFFIC_MULTIPLIER;

            if (!availableBidModifiersTypes.has(trafficApiType)) {
                return undefined;
            }

            const trafficData = rateCorrectionsFromApi.find(
                rateCorrectionFromApi => rateCorrectionFromApi !== null && rateCorrectionFromApi.type === trafficApiType
            );

            if (trafficData === undefined || trafficData.type !== GdBidModifierType.EXPRESS_TRAFFIC_MULTIPLIER) {
                return {
                    type: RateCorrectionType.trafficJam,
                    items: []
                };
            }

            const trafficAdjustments = trafficData.adjustments.map(
                (
                    adjustment: adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierExpressAdjustment
                ) => {
                    return {
                        id: adjustment.id,
                        serverId: adjustment.id,
                        value: getRateCorrectionValueFromMultiplier(adjustment.percent),
                        trafficJamType: mapTrafficTypeConditionsFromApi(adjustment.condition[0][0].value)
                    };
                }
            );

            return {
                type: RateCorrectionType.trafficJam,
                items: trafficAdjustments
            };
        }

        default:
            throw Error(`Неподдерживаемый тип корректировки ставки: ${type}`);
    }
}

export const mapWeatherConditionsDataFromApiForAllDone = (
    adjustments: adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierWeatherAdjustment[]
): RateCorrection => {
    const adjustmentItem = adjustments[0];
    const value = getRateCorrectionValueFromMultiplier(adjustmentItem.percent);

    return {
        type: RateCorrectionType.weather,
        mode: RateCorrectionMode.allDone,
        singleMassRateControlValue: value,
        items: mapWeatherConditionsFromWeatherAdjustmentItem(adjustmentItem, true)
    };
};

export const mapWeatherConditionsDataFromApiForOneDone = (
    adjustments: adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierWeatherAdjustment[]
): RateCorrection => {
    return {
        type: RateCorrectionType.weather,
        mode: RateCorrectionMode.oneDone,
        items: adjustments.reduce((acc: WeatherRateCorrectionCondition[], item) => {
            const listWeatherRateCorrectionCondition = mapWeatherConditionsFromWeatherAdjustmentItem(item, false);

            acc = acc.concat(listWeatherRateCorrectionCondition);

            return acc;
        }, [])
    };
};

export const mapWeatherConditionsFromWeatherAdjustmentItem = (
    adjustmentItem: adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers_adjustments_GdBidModifierWeatherAdjustment,
    isAllDoneWeatherConditions: boolean
): WeatherRateCorrectionCondition[] => {
    const value = getRateCorrectionValueFromMultiplier(adjustmentItem.percent);
    const expression = adjustmentItem.expression;

    if (!expression) {
        return [];
    }

    const weatherMapper: Partial<Record<WeatherRateCorrectionsType, number[]>> = {};

    expression.forEach(expressionItem => {
        if (expressionItem) {
            expressionItem.forEach(item => {
                const clientWeatherType = WeatherRateCorrectionEqualTypesFromApi[item.parameter];

                const currentListValues = weatherMapper[clientWeatherType] || [];

                weatherMapper[clientWeatherType] = [...currentListValues, item.value];
            });
        }
    });

    return Object.keys(weatherMapper).map((clientWeatherType: WeatherRateCorrectionsType) => {
        const valueItem = weatherMapper[clientWeatherType];

        switch (clientWeatherType) {
            case WeatherRateCorrectionsType.temperature:
                if (!valueItem || valueItem.length !== 2) {
                    throw new Error('Ошибка данных api, для корректировки погоды: температура');
                }

                const resultItemTemperature: WeatherRateCorrectionConditionTemperature = {
                    id: getRandomId(),
                    serverId: adjustmentItem.id,
                    value: !isAllDoneWeatherConditions ? value : undefined,
                    weatherType: WeatherRateCorrectionsType.temperature,
                    minTemperature: Math.min(...valueItem),
                    maxTemperature: Math.max(...valueItem)
                };

                return resultItemTemperature;
            case WeatherRateCorrectionsType.rainfall:
                if (!valueItem || valueItem.length === 0) {
                    throw new Error('Ошибка данных api, для корректировки погоды: осадки');
                }

                const resultItemRainfall: WeatherRateCorrectionConditionRainfall = {
                    id: getRandomId(),
                    serverId: adjustmentItem.id,
                    value: !isAllDoneWeatherConditions ? value : undefined,
                    weatherType: WeatherRateCorrectionsType.rainfall,
                    rainfallIds: valueItem.reduce((acc: RainfallTypes[], item) => {
                        const rainfallType = rainfallTypesByValue[item];

                        if (rainfallType !== undefined) {
                            acc.push(rainfallType);
                        }

                        return acc;
                    }, [])
                };

                return resultItemRainfall;
            case WeatherRateCorrectionsType.cloudiness:
                if (!valueItem || valueItem.length === 0) {
                    throw new Error('Ошибка данных api, для корректировки погоды: облачность');
                }

                const resultItemCloudiness: WeatherRateCorrectionConditionCloudiness = {
                    id: getRandomId(),
                    serverId: adjustmentItem.id,
                    value: !isAllDoneWeatherConditions ? value : undefined,
                    weatherType: WeatherRateCorrectionsType.cloudiness,
                    cloudinessIds: valueItem.reduce((acc: CloudinessTypes[], item) => {
                        const cloudinessType = cloudinessTypesByValue[item];

                        if (cloudinessType !== undefined) {
                            acc.push(cloudinessType);
                        }

                        return acc;
                    }, [])
                };

                return resultItemCloudiness;
            default:
                throw new Error('Не поддерживаемый тип погоды');
        }
    });
};

export const WeatherRateCorrectionEqualTypesFromApi = {
    [GdWeatherType.TEMP]: WeatherRateCorrectionsType.temperature,
    [GdWeatherType.CLOUDNESS]: WeatherRateCorrectionsType.cloudiness,
    [GdWeatherType.PREC_STRENGTH]: WeatherRateCorrectionsType.rainfall
};

export function mapRatesCorrectionsValueToApiFormat(
    corrections: RatesCorrectionsValue,
    campaignId: string,
    groupId?: string | null
): GdUpdateBidModifiersInput {
    return Object.keys(corrections).reduce(
        (res: GdUpdateBidModifiersInput, correctionKey: RateCorrectionType): GdUpdateBidModifiersInput => {
            const correction = corrections[correctionKey];
            const baseParams = {
                adGroupId: groupId,
                campaignId: campaignId,
                enabled: correction.isSwitchedOn !== false
            };

            if (correction.items.length === 0) {
                return res;
            }

            switch (correction.type) {
                case RateCorrectionType.retargeting: {
                    res.bidModifierRetargeting = {
                        ...baseParams,
                        adjustments: correction.items.map((item: RetargetingRateCorrectionCondition) => {
                            return {
                                percent: getMultiplierFromRateCorrectionValue(item.value),
                                retargetingConditionId: item.retargetingConditionId,
                                id: item.serverId
                            };
                        }),
                        type: GdBidModifierTypeInput.RETARGETING_MULTIPLIER
                    };

                    break;
                }

                case RateCorrectionType.demography: {
                    res.bidModifierDemographics = {
                        ...baseParams,
                        adjustments: correction.items.map((item: DemographyRateCorrectionCondition) => {
                            return {
                                percent: getMultiplierFromRateCorrectionValue(item.value),
                                id: item.serverId,
                                age: mapRateCorrectionAgeToApiFormat(item.age),
                                gender: mapRateCorrectionGenderToApiFormat(item.gender)
                            };
                        }),
                        type: GdBidModifierTypeInput.DEMOGRAPHY_MULTIPLIER
                    };

                    break;
                }

                case RateCorrectionType.videoAddition: {
                    const itemData = correction.items[0];

                    if (itemData !== undefined) {
                        res.bidModifierVideo = {
                            ...baseParams,
                            adjustment: {
                                percent: getMultiplierFromRateCorrectionValue(itemData.value),
                                id: itemData.serverId
                            },
                            type: GdBidModifierTypeInput.VIDEO_MULTIPLIER
                        };
                    }

                    break;
                }

                case RateCorrectionType.smartBanners: {
                    const itemData = correction.items[0];

                    if (itemData !== undefined) {
                        res.bidModifierSmartTgo = {
                            ...baseParams,
                            adjustment: {
                                percent: getMultiplierFromRateCorrectionValue(itemData.value),
                                id: itemData.serverId
                            },
                            type: GdBidModifierTypeInput.SMART_TGO_MULTIPLIER
                        };
                    }

                    break;
                }

                case RateCorrectionType.device: {
                    const desktopItem = correction.items.find(
                        item => item.deviceType === DeviceRateCorrectionType.desktops
                    );
                    const allOsType = new Set([
                        DeviceRateCorrectionType.allMobile,
                        DeviceRateCorrectionType.android,
                        DeviceRateCorrectionType.ios
                    ]);
                    const mobileItem = correction.items.find(item => allOsType.has(item.deviceType));

                    if (desktopItem) {
                        res.bidModifierDesktop = {
                            ...baseParams,
                            adjustment: {
                                percent: getMultiplierFromRateCorrectionValue(desktopItem.value),
                                id: desktopItem.serverId
                            },
                            type: GdBidModifierTypeInput.DESKTOP_MULTIPLIER
                        };
                    }

                    if (mobileItem) {
                        res.bidModifierMobile = {
                            ...baseParams,
                            adjustment: {
                                percent: getMultiplierFromRateCorrectionValue(mobileItem.value),
                                id: mobileItem.serverId,
                                osType: mapDevicesTypesToApiFormat(mobileItem.deviceType)
                            },
                            type: GdBidModifierTypeInput.MOBILE_MULTIPLIER
                        };
                    }

                    break;
                }

                case RateCorrectionType.inventory: {
                    const inventoryType = new Set(INVENTORY_VIDEO_CORRECTIONS);
                    const adType = new Set(INVENTORY_AD_TYPE_CORRECTIONS);

                    const inventoryItems = correction.items.filter(item => inventoryType.has(item.inventoryType));
                    const adItems = correction.items.filter(item => adType.has(item.inventoryType));

                    if (inventoryItems.length) {
                        res.bidModifierInventory = {
                            ...baseParams,
                            type: GdBidModifierTypeInput.INVENTORY_MULTIPLIER,
                            adjustments: inventoryItems.map(item => ({
                                percent: getMultiplierFromRateCorrectionValue(item.value),
                                id: item.serverId,
                                inventoryType: mapRateCorrectionInventoryTypeToApiFormat(item.inventoryType)
                            }))
                        };
                    }

                    if (adItems) {
                        res.bidModifierAdType = {
                            ...baseParams,
                            type: GdBidModifierTypeInput.AD_TYPE_MULTIPLIER,
                            adjustments: adItems.map(item => ({
                                percent: getMultiplierFromRateCorrectionValue(item.value),
                                id: item.serverId,
                                adType: mapRateCorrectionInventoryTypeToApiAdTypeFormat(item.inventoryType)
                            }))
                        };
                    }

                    break;
                }

                case RateCorrectionType.weather:
                    res.bidModifierWeather = {
                        ...baseParams,
                        adjustments:
                            correction.mode === RateCorrectionMode.allDone
                                ? mapWeatherCorrectionDataToApiFormatForAllDone(correction)
                                : mapWeatherCorrectionDataToApiFormatForOneDone(correction),
                        type: GdBidModifierTypeInput.WEATHER_MULTIPLIER
                    };

                    break;

                case RateCorrectionType.trafficJam:
                    const trafficItem = correction.items.map(item => {
                        return {
                            percent: getMultiplierFromRateCorrectionValue(item.value || 0),
                            id: item.serverId,
                            condition: [
                                [
                                    {
                                        operation: BidModifierExpressionOperatorInput.EQ,
                                        parameter: BidModifierExpressionParameterInput.TRAFFIC_JAM,
                                        value: trafficValuesByType[item.trafficJamType]
                                    }
                                ]
                            ]
                        };
                    });

                    if (trafficItem !== undefined) {
                        res.bidModifierExpress = [
                            {
                                ...baseParams,
                                adjustments: trafficItem,
                                type: GdBidModifierTypeInput.EXPRESS_TRAFFIC_MULTIPLIER
                            }
                        ];
                    }

                    break;

                default:
                    break;
            }

            return res;
        },
        {} as GdUpdateBidModifiersInput
    );
}

export const mapRatesCorrectionsValueToApiFormatForGroup = (
    corrections: RatesCorrectionsValue,
    groupId: string,
    campaignId: string,
    isNew: boolean
): GdUpdateBidModifiersInput => {
    return mapRatesCorrectionsValueToApiFormat(corrections, campaignId, isNew ? null : groupId);
};

export const mapRatesCorrectionsValueToApiFormatForCampaign = (
    corrections: RatesCorrectionsValue,
    campaignId: string
): GdUpdateBidModifiersInput => {
    return mapRatesCorrectionsValueToApiFormat(corrections, campaignId);
};

export const mapWeatherConditionsItemsToApiExpression = (
    items: WeatherRateCorrectionCondition[]
): GdUpdateBidModifierWeatherExpressionInput[][] => {
    const resultExpression: GdUpdateBidModifierWeatherExpressionInput[][] = [];

    items.forEach(item => {
        switch (item.weatherType) {
            case WeatherRateCorrectionsType.temperature:
                resultExpression.push([
                    {
                        operation: GdOperationTypeInput.GE,
                        parameter: GdWeatherTypeInput.TEMP,
                        value: item.minTemperature
                    }
                ]);

                resultExpression.push([
                    {
                        operation: GdOperationTypeInput.LE,
                        parameter: GdWeatherTypeInput.TEMP,
                        value: item.maxTemperature
                    }
                ]);

                break;
            case WeatherRateCorrectionsType.rainfall:
                resultExpression.push(
                    item.rainfallIds.map(rainfallId => {
                        return {
                            operation: GdOperationTypeInput.EQ,
                            parameter: GdWeatherTypeInput.PREC_STRENGTH,
                            value: rainfallValuesByType[rainfallId]
                        };
                    })
                );

                break;
            case WeatherRateCorrectionsType.cloudiness:
                resultExpression.push(
                    item.cloudinessIds.map(cloudinessId => {
                        return {
                            operation: GdOperationTypeInput.EQ,
                            parameter: GdWeatherTypeInput.CLOUDNESS,
                            value: cloudinessValuesByType[cloudinessId]
                        };
                    })
                );

                break;
            default:
                throw new Error('Неподдерживаемый тип корректировки погоды');
        }
    });

    return resultExpression;
};

export const mapWeatherCorrectionDataToApiFormatForAllDone = (
    correction: WeatherRateCorrection
): GdUpdateBidModifierWeatherAdjustmentItemInput[] => {
    if (correction.items.length === 0 || correction.singleMassRateControlValue === undefined) {
        return [];
    }

    return [
        {
            id: correction.items[0].serverId,
            percent: getMultiplierFromRateCorrectionValue(correction.singleMassRateControlValue),
            expression: mapWeatherConditionsItemsToApiExpression(correction.items)
        }
    ];
};

export const mapWeatherCorrectionDataToApiFormatForOneDone = (
    correction: WeatherRateCorrection
): GdUpdateBidModifierWeatherAdjustmentItemInput[] => {
    if (correction.items.length === 0) {
        return [];
    }

    return correction.items.reduce((acc: GdUpdateBidModifierWeatherAdjustmentItemInput[], correctionItem) => {
        if (correctionItem.value !== undefined) {
            acc.push({
                id: correctionItem.serverId,
                percent: getMultiplierFromRateCorrectionValue(correctionItem.value),
                expression: mapWeatherConditionsItemsToApiExpression([correctionItem])
            });
        }

        return acc;
    }, []);
};

export const mapRatesCorrectionsTypeFromApiFormat = (
    type: GdBidModifierType
): RateCorrectionType | ExtraRateCorrectionType | null => {
    switch (type) {
        case GdBidModifierType.RETARGETING_MULTIPLIER:
            return RateCorrectionType.retargeting;

        case GdBidModifierType.DEMOGRAPHY_MULTIPLIER:
            return RateCorrectionType.demography;

        case GdBidModifierType.SMART_TGO_MULTIPLIER:
            return RateCorrectionType.smartBanners;

        case GdBidModifierType.VIDEO_MULTIPLIER:
            return RateCorrectionType.videoAddition;

        case GdBidModifierType.WEATHER_MULTIPLIER:
            return RateCorrectionType.weather;

        case GdBidModifierType.DESKTOP_MULTIPLIER:
        case GdBidModifierType.MOBILE_MULTIPLIER:
            return RateCorrectionType.device;

        case GdBidModifierType.INVENTORY_MULTIPLIER:
        case GdBidModifierType.AD_TYPE_MULTIPLIER:
            return RateCorrectionType.inventory;

        case GdBidModifierType.AB_SEGMENT_MULTIPLIER:
            return ExtraRateCorrectionType.abSegments;

        case GdBidModifierType.EXPRESS_TRAFFIC_MULTIPLIER:
            return RateCorrectionType.trafficJam;

        default:
            return null;
    }
};

export const mapDevicesTypesFromApiFormat = (
    osTypes: GdOsType[],
    isDeviceModifiersAllowed: boolean
): DeviceRateCorrectionType[] => {
    const optionsType = new Set([GdOsType.ANDROID, GdOsType.IOS]);
    const osAvalibleTypes = isDeviceModifiersAllowed ? osTypes : osTypes.filter(type => !optionsType.has(type));

    return osAvalibleTypes.map(element => {
        switch (element) {
            case GdOsType.ANDROID:
                return DeviceRateCorrectionType.android;
            case GdOsType.IOS:
                return DeviceRateCorrectionType.ios;
            default:
                return element;
        }
    });
};

export const mapDevicesTypesToApiFormat = (osType: DeviceRateCorrectionType | undefined): GdOsTypeInput | null => {
    switch (osType) {
        case DeviceRateCorrectionType.ios:
            return GdOsTypeInput.IOS;

        case DeviceRateCorrectionType.android:
            return GdOsTypeInput.ANDROID;

        case undefined:
        default:
            return null;
    }
};

export const mapRatesCorrectionsConstantsFromApiFormat = (
    allowedRatesCorrectionsValues: (GroupsQueryForEdit_client_clientConstants_allowedBidModifiersValues)[],
    isDeviceModifiersAllowed: boolean
): RatesCorrectionsConstantsData => {
    let desktopParams: BaseRatesCorrectionsConstants;
    let inventoryParams: RatesCorrectionsInventoryConstants;

    return allowedRatesCorrectionsValues.reduce(
        (
            acc: RatesCorrectionsConstantsData,
            elem: GroupsQueryForEdit_client_clientConstants_allowedBidModifiersValues
        ) => {
            const type = mapRatesCorrectionsTypeFromApiFormat(elem.type);
            const baseParams = {
                maxConditions: elem.maxConditions,
                percentRange: {
                    max: getRateCorrectionValueFromMultiplier(elem.percentRange.max),
                    min: getRateCorrectionValueFromMultiplier(elem.percentRange.min),
                    step: elem.percentRange.step
                }
            };

            if (type !== null) {
                switch (elem.__typename) {
                    case 'GdAllowedBidModifierValuesDesktop': {
                        // Если включена фича или открыты охватные, достаем значение из десктопов
                        // Решает проблему неверных значений для корректировок DIRECT-105502
                        if (isDeviceModifiersAllowed) {
                            const currentDevice = acc[type] as RatesCorrectionsMobileConstants;
                            desktopParams = baseParams;

                            acc[type] = {
                                ...currentDevice,
                                ...baseParams
                            };
                        }

                        break;
                    }

                    case 'GdAllowedBidModifierValuesMobile': {
                        acc[type] = {
                            ...(desktopParams || baseParams),
                            type: RateCorrectionType.device,
                            osTypes: mapDevicesTypesFromApiFormat(elem.osTypes, isDeviceModifiersAllowed)
                        };

                        break;
                    }

                    case 'GdAllowedBidModifierValuesDemographics': {
                        acc[type] = {
                            ...baseParams,
                            type: RateCorrectionType.demography
                        };

                        break;
                    }

                    case 'GdAllowedBidModifierValuesRetargeting': {
                        acc[type] = {
                            ...baseParams,
                            type: RateCorrectionType.retargeting
                        };

                        break;
                    }

                    case 'GdAllowedBidModifierValuesSmartTgo': {
                        acc[type] = {
                            ...baseParams,
                            type: RateCorrectionType.smartBanners
                        };

                        break;
                    }

                    case 'GdAllowedBidModifierValuesVideo': {
                        acc[type] = {
                            ...baseParams,
                            type: RateCorrectionType.videoAddition
                        };

                        break;
                    }

                    case 'GdAllowedBidModifierValuesWeather': {
                        acc[type] = {
                            ...baseParams,
                            type: RateCorrectionType.weather
                        };

                        break;
                    }

                    case 'GdAllowedBidModifierValuesABSegment': {
                        acc[type] = {
                            ...baseParams,
                            type: ExtraRateCorrectionType.abSegments
                        };

                        break;
                    }

                    case 'GdAllowedBidModifierValuesExpression': {
                        acc[type] = {
                            ...baseParams,
                            type: RateCorrectionType.trafficJam
                        };

                        break;
                    }

                    case 'GdAllowedBidModifierValuesInventory': {
                        const params: RatesCorrectionsInventoryConstants = {
                            ...(inventoryParams || baseParams),
                            maxConditions: inventoryParams
                                ? inventoryParams.maxConditions + elem.maxConditions
                                : elem.maxConditions,
                            type: RateCorrectionType.inventory,
                            inventoryTypes: inventoryParams
                                ? inventoryParams.inventoryTypes.concat(
                                      mapInventoryTypesFromApiFormat(elem.inventoryTypes)
                                  )
                                : mapInventoryTypesFromApiFormat(elem.inventoryTypes)
                        };

                        acc[type] = params;

                        if (!inventoryParams) {
                            inventoryParams = params;
                        }

                        break;
                    }

                    case 'GdAllowedBidModifierValuesAdType': {
                        const params: RatesCorrectionsInventoryConstants = {
                            ...(inventoryParams || baseParams),
                            maxConditions: inventoryParams
                                ? inventoryParams.maxConditions + elem.maxConditions
                                : elem.maxConditions,
                            type: RateCorrectionType.inventory,
                            inventoryTypes: inventoryParams
                                ? inventoryParams.inventoryTypes.concat(mapAdTypesFromApiFormat(elem.adTypes))
                                : mapAdTypesFromApiFormat(elem.adTypes)
                        };

                        acc[type] = params;

                        if (!inventoryParams) {
                            inventoryParams = params;
                        }

                        break;
                    }

                    default: {
                        return acc;
                    }
                }
            }

            return acc;
        },
        {} as RatesCorrectionsConstantsData
    );
};

export const mapAdTypesFromApiFormat = (adTypes: GdAdjustmentAdType[]): InventoryRateCorrectionType[] => {
    return adTypes.reduce((result: InventoryRateCorrectionType[], element: GdAdjustmentAdType) => {
        switch (element) {
            case GdAdjustmentAdType.CPM_BANNER:
                return result.concat(InventoryRateCorrectionType.cpmBanner);

            default:
                return result;
        }
    }, []);
};

export const mapInventoryTypesFromApiFormat = (inventoryTypes: GdInventoryType[]): InventoryRateCorrectionType[] => {
    return inventoryTypes.map(element => {
        switch (element) {
            case GdInventoryType.INBANNER:
                return InventoryRateCorrectionType.inbanner;

            case GdInventoryType.INPAGE:
                return InventoryRateCorrectionType.inpage;

            case GdInventoryType.INSTREAM_WEB:
                return InventoryRateCorrectionType.instreamWeb;

            case GdInventoryType.INTERSTITIAL:
                return InventoryRateCorrectionType.interstitial;

            case GdInventoryType.REWARDED:
                return InventoryRateCorrectionType.rewarded;

            default:
                return element;
        }
    });
};

export const getAllowedRateCorrectionsDataForCampaign = (
    allowedBidModifierTypesByCampaignTypes: CampaignsEditDataQuery_client_clientConstants_allowedBidModifierTypesByCampaignTypes[]
): Record<CampaignType, Array<GdBidModifierType>> => {
    return allowedBidModifierTypesByCampaignTypes.reduce(
        (
            res: Record<CampaignType, Array<GdBidModifierType>>,
            item: CampaignsEditDataQuery_client_clientConstants_allowedBidModifierTypesByCampaignTypes
        ): Record<CampaignType, Array<GdBidModifierType>> => {
            if (item === null) {
                return res;
            }

            res[mapCampaignRowTypeToCampaignType(item.campaignType)] = item.allowedBidModifierTypes;

            return res;
        },
        {} as Record<CampaignType, Array<GdBidModifierType>>
    );
};

export const getAllowedRateCorrectionsDataForGroup = (
    allowedBidModifierTypesByAdGroupTypes: GroupsQueryForEdit_client_clientConstants_allowedBidModifierTypesByAdGroupTypes[]
): Record<GroupType, Array<GdBidModifierType>> => {
    return allowedBidModifierTypesByAdGroupTypes.reduce(
        (
            res: Record<GroupType, Array<GdBidModifierType>>,
            item: GroupsQueryForEdit_client_clientConstants_allowedBidModifierTypesByAdGroupTypes
        ): Record<GroupType, Array<GdBidModifierType>> => {
            if (item === null) {
                return res;
            }

            if (item.adGroupType === GdAdGroupType.CPM_BANNER) {
                res[mapGroupRowTypeToGroupType(item.adGroupType)] = [
                    GdBidModifierType.WEATHER_MULTIPLIER,
                    GdBidModifierType.SMART_TGO_MULTIPLIER,
                    GdBidModifierType.DESKTOP_MULTIPLIER,
                    GdBidModifierType.MOBILE_MULTIPLIER,
                    GdBidModifierType.VIDEO_MULTIPLIER,
                    GdBidModifierType.RETARGETING_MULTIPLIER,
                    GdBidModifierType.DEMOGRAPHY_MULTIPLIER
                ];
            } else {
                res[mapGroupRowTypeToGroupType(item.adGroupType)] = item.allowedBidModifierTypes;
            }

            return res;
        },
        {} as Record<GroupType, Array<GdBidModifierType>>
    );
};

export const mapTrafficTypeConditionsFromApi = (type: string): TrafficJamRateCorrectionsType => {
    const mapType = {
        '0': TrafficJamRateCorrectionsType.free,
        '1': TrafficJamRateCorrectionsType.light,
        '2': TrafficJamRateCorrectionsType.hard,
        '3': TrafficJamRateCorrectionsType.veryHard
    };

    return mapType[type];
};
